import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import HotelSettingsMyotbMyotbBaseController from 'lumen/controllers/hotel/settings/myotb/myotb-base';
import {
  ELIGIBILITY_QUESTION_TYPES,
  type EligibilityQuestionType,
  STAGES,
} from 'lumen/utils/constants/otb-selfservice-constants';

type Answer = string;

type EligibilityQuestion = {
  answers: Answer[];
  name: string;
  options: string[];
  questionType: EligibilityQuestionType;
};

type PmsdatasourceEligibilityQuestion = Omit<
  EligibilityQuestion,
  'questionType'
> & {
  question_type: EligibilityQuestionType;
};

export default class HotelSettingsMyotbPmsEligibilityController extends HotelSettingsMyotbMyotbBaseController {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];

  @tracked eligibilityQuestions: EligibilityQuestion[] | null = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eligibilityQuestionsAnswers() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.otbSelfService.eligibilityQuestionsAnswers;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsDatasource() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.otbSelfService.pmsDatasource;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async onClickContinue() {
    // Set selected properties on the local self-service object
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.otbSelfService.eligibilityQuestionsAnswers =
      // @ts-expect-error: Incorrect type
      this.eligibilityQuestions.map((eligibilityQuestion) => {
        const { answers, name } = eligibilityQuestion;

        return { answers, name };
      });

    // Save the self-service object
    try {
      this.isLoading = true;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.otbSelfService.save();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { stage } = this.otbSelfService;

      // Redirect based on the updated otb self-service object returned by the API in the save call
      if (
        (stage === STAGES.PMS_SETUP_PAGE ||
          stage === STAGES.VALIDATING_DATA_PAGE) &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        !this.otbSelfService.otherSystem
      ) {
        // User's PMS setup is supported and no other system was selected previously
        // (e.g. the user went back from the setup page or validation page and selects the same PMS and PMS eligibility questions again).
        this.router.transitionTo('hotel.settings.myotb.pms-setup');
      } else {
        // User's PMS setup is unsupported.
        this.router.transitionTo('hotel.settings.myotb.other-system-selection');
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action onClickPrevious(): void {
    this.router.transitionTo('hotel.settings.myotb.pms-selection');
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setAnswers(eligibilityQuestionName: unknown, answers: Answer[]) {
    // Create a new eligibility questions array to trigger state updates.
    const newEligibilityQuestions = [...this.eligibilityQuestions!];

    // Create a new eligibility question object to trigger state updates.
    const index = newEligibilityQuestions.findIndex((question) => {
      return question.name === eligibilityQuestionName;
    });

    const newEligibilityQuestion = {
      ...newEligibilityQuestions[index]!,
      answers,
    };

    newEligibilityQuestions[index] = newEligibilityQuestion;

    this.eligibilityQuestions = newEligibilityQuestions;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setDefaults() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { eligibilityQuestions } = this.pmsDatasource;

    if (!eligibilityQuestions) {
      return;
    }

    this.eligibilityQuestions = (
      eligibilityQuestions as PmsdatasourceEligibilityQuestion[]
    ).map((eligibilityQuestion) => {
      const {
        name,
        options,
        question_type: questionType,
      } = eligibilityQuestion;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const answers: Answer[] =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.eligibilityQuestionsAnswers?.find((answer) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return answer.name === eligibilityQuestion.name;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        })?.answers ?? [];

      // Question type single requires that at least one of the options is selected, so push the first option to the answers.
      if (
        questionType === ELIGIBILITY_QUESTION_TYPES.SINGLE &&
        answers.length === 0
      ) {
        answers.push(options[0]!);
      }

      return {
        answers,
        name,
        options,
        questionType,
      };
    });
  }
}
